import React, { useEffect, useRef } from 'react';
import Button from '../Button/Button';
import styles from './BaseDialog.module.css';
import CloseIcon from '../../Icons/CloseIcon';
/**
 * A beautiful loading spinner for dialog actions.
 */
const Spinner = () => (
  <svg className={styles.spinner} viewBox="0 0 50 50" width="16" height="16">
    <circle
      className={styles.path}
      cx="25"
      cy="25"
      r="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="5"
    />
  </svg>
);

/** Fixed max-widths, in px. The dialog is centered and capped at these. */
const SIZE_MAP = {
  xs: 400,
  small: 520,
  medium: 660,
  large: 900,
  xlarge: 1100,
  full: 1400,
};

/** Gutter left on each side by `size="fullWidth"`. */
const FULL_WIDTH_GUTTER = 24;

/**
 * Sizes that aren't a simple max-width cap.
 *
 * `fullWidth` spans the whole viewport minus a gutter with NO cap — unlike
 * `full`, which is still capped at 1400px. Width is `100%` rather than `100vw`
 * so it resolves against the dialog's containing block and can't overflow by
 * the scrollbar width.
 */
const SIZE_STYLE_MAP = {
  fullWidth: {
    maxWidth: 'none',
    width: `calc(100% - ${FULL_WIDTH_GUTTER * 2}px)`,
  },
};

/**
 * @typedef {Object} BaseDialogCustomProps
 * @property {boolean} [open] - Controls the visibility of the dialog.
 * @property {() => void} [onClose] - Callback fired when dialog is requested to close.
 * @property {() => void} [onConfirm] - Callback fired when the primary/confirm button is clicked.
 * @property {() => void} [onCancel] - Callback fired when the cancel/secondary button is clicked.
 * @property {string} [title] - The title text displayed in the header.
 * @property {string} [confirmLabel] - Label for the primary action button. Defaults to 'Save'.
 * @property {string} [cancelLabel] - Label for the secondary cancel button. Defaults to 'Cancel'.
 * @property {boolean} [hideCancel] - If true, hides the cancel/secondary button. Defaults to false.
 * @property {boolean} [hideConfirm] - If true, hides the primary/confirm button. Defaults to false.
 * @property {boolean} [hideCloseButton] - If true, hides the close icon in the header. Defaults to false.
 * @property {boolean} [loading] - If true, shows a spinner inside the primary button and disables all buttons.
 * @property {boolean} [disabled] - If true, disables all action buttons.
 * @property {boolean} [confirmDisabled] - If true, selectively disables ONLY the confirm button.
 * @property {'xs'|'small'|'medium'|'large'|'xlarge'|'full'|'fullWidth'} [size] - Preset width. `xs`–`full` cap max-width at 400/520/660/900/1100/1400px. `fullWidth` spans the viewport minus a 24px gutter each side with no cap.
 * @property {boolean} [staticBackdrop] - If true, clicking the backdrop or pressing Escape will not close the dialog. Defaults to false.
 * @property {boolean} [centerText] - If true, centers the dialog body text. Defaults to false.
 * @property {'small'|'medium'|'large'} [confirmSize] - Size forwarded to the confirm Button. Defaults to 'medium'.
 * @property {'small'|'medium'|'large'} [cancelSize] - Size forwarded to the cancel Button. Defaults to 'medium'.
 * @property {string} [color] - Overrides the dialog title and primary button color. Unset by default, so the theme's own primary color (via Button's CSS-variable-driven default) is used instead.
 * @property {React.ReactNode} [children] - Main slot for body/content elements.
 * @property {React.ReactNode} [headerActions] - Optional actions (e.g. icon buttons) rendered in the header, between the title and the close button.
 * @property {string} [className] - Optional custom class name for external styling.
 * @property {string} [containerClassName] - Optional custom class name for the inner body container.
 * @property {React.CSSProperties} [style] - Inline styles applied directly to the dialog container.
 * @property {React.ReactNode} [footerLeftContent] - Custom slot in the footer to show message labels (e.g. errors, statuses).
 * @property {React.ElementType} [PaperComponent] - Container tag type, e.g. 'div' or 'form'.
 * @property {Object} [paperProps] - Props applied directly to the PaperComponent.
 * 
 * @typedef {import('react').DialogHTMLAttributes<HTMLDialogElement> & BaseDialogCustomProps} BaseDialogProps
 */

/**
 * BaseDialog component using HTML5 native <dialog> with premium styling,
 * keyboard controls, backdrop clicks, and rich customization features.
 * 
 * @param {BaseDialogProps} props
 * @returns {React.ReactElement}
 */
const BaseDialog = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  title,
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  hideCancel = false,
  hideConfirm = false,
  hideCloseButton = false,
  staticBackdrop = false,
  loading = false,
  disabled = false,
  confirmDisabled = false,
  size,
  children,
  headerActions,
  className = '',
  containerClassName = '',
  style,
  footerLeftContent,
  PaperComponent = 'div',
  paperProps = {},
  centerText = false,
  confirmSize = 'medium',
  cancelSize = 'medium',
  color,
  ...rest
}) => {
  const dialogRef = useRef(null);

  const isOpen = open;
  const handleConfirmAction = onConfirm;
  const handleCancelAction = onCancel || onClose;

  const isConfirmDisabled = disabled || confirmDisabled || loading;

  const presetSizeStyle = SIZE_MAP[size] != null
    ? { maxWidth: SIZE_MAP[size], width: '100%' }
    : SIZE_STYLE_MAP[size];

  // Caller `style` still wins, so a one-off width override stays possible.
  const sizeStyle = presetSizeStyle ? { ...presetSizeStyle, ...style } : style;
  const sizePaperProps = presetSizeStyle
    ? { ...paperProps, style: { width: '100%', ...paperProps.style } }
    : paperProps;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      if (!dialog.open) {
        // Older WebViews (e.g. WKWebView pre-iOS 15.4) ship <dialog> without
        // showModal() - falling through to the non-modal open attribute at
        // least makes the content visible instead of silently showing nothing.
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        // Keep focus on the dialog itself rather than on the first button
        // showModal picks: a button focused with no preceding tap renders its
        // focus ring (iOS shows it as a grey, outlined button).
        if (typeof dialog.focus === 'function') {
          dialog.focus();
        }
      }
    } else {
      if (dialog.open) {
        if (typeof dialog.close === 'function') {
          dialog.close();
        } else {
          dialog.removeAttribute('open');
        }
      }
    }
  }, [isOpen]);

  // Handle native escape keyboard action
  const handleCancel = (e) => {
    e.preventDefault();
    if (!staticBackdrop) handleCancelAction?.();
  };

  // Close when clicking directly on the backdrop
  const handleBackdropClick = (e) => {
    if (!staticBackdrop && e.target === dialogRef.current) {
      handleCancelAction?.();
    }
  };

  // Enter confirms while focus sits on the dialog itself (where it is left on
  // open). Clicking the button rather than calling onConfirm keeps the
  // FormDialog submit path intact.
  const handleKeyDown = (e) => {
    if (typeof rest.onKeyDown === 'function') rest.onKeyDown(e);
    if (e.defaultPrevented || e.key !== 'Enter' || e.target !== dialogRef.current) {
      return;
    }
    if (hideConfirm || isConfirmDisabled) return;
    e.preventDefault();
    const confirmButton = dialogRef.current.querySelector('.confirm-button');
    if (confirmButton) confirmButton.click();
  };

  const Paper = PaperComponent;

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${className} base-dialog-root`}
      style={sizeStyle}
      tabIndex={-1}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      {...rest}
      onKeyDown={handleKeyDown}
    >
      <Paper
        className={`${styles.paper} base-dialog-paper`}
        {...sizePaperProps}
      >
        {/* Header Section */}
        <div className={`${styles.header} base-dialog-header ${!title ? styles.noTitle : ''}`}>
          {title && (
            <span
              className={`${styles.title} base-dialog-title`}
              style={color ? { color } : undefined}
            >
              {title}
            </span>
          )}
          <div className={`${styles.headerRight} base-dialog-header-right`}>
            {headerActions && (
              <div className={`${styles.headerActions} base-dialog-header-actions`}>
                {headerActions}
              </div>
            )}
            {!hideCloseButton && (
              <button
                type="button"
                className={`${styles.closeButton} base-dialog-close-btn`}
                onClick={handleCancelAction}
                aria-label="close"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Body Content */}
        <div className={`${styles.content} ${centerText ? styles.centerText : ''} ${containerClassName} base-dialog-content`}>
          {children}
        </div>

        {/* Footer Actions */}
        <div className={`${styles.footer} base-dialog-footer ${footerLeftContent ? styles.footerWithMessage : ''}`}>
          {footerLeftContent && (
            <div className={`${styles.footerLeft} base-dialog-footer-left`}>
              {footerLeftContent}
            </div>
          )}
          <div className={`${styles.actions} base-dialog-actions`}>
            {!hideCancel && (
              <Button
                white
                className='cancel-button'
                size={cancelSize}
                onClick={handleCancelAction}
                disabled={disabled}
              >
                {cancelLabel}
              </Button>
            )}
            {!hideConfirm && (
              <Button
                className='confirm-button'
                size={confirmSize}
                color={color}
                onClick={handleConfirmAction}
                disabled={isConfirmDisabled}
                type={rest.type || "button"}
              >
                {loading ? <Spinner color={'#fff'} /> : confirmLabel}
              </Button>
            )}
          </div>
        </div>
      </Paper>
    </dialog>
  );
};

export default BaseDialog;
